import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors
 * @property {HTMLElement[]} cartItemRows
 * @property {TextComponent} cartTotal
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
  if (!(event.target instanceof Node) || !this.contains(event.target)) return;

  const { quantity, cartLine: line } = event.detail;

  if (!line) return;

  if (quantity === 0) {
    return this.onLineItemRemove(line);
  }

  this.updateQuantity({
    line,
    quantity,
    action: 'change',
  });

  const lineItemRow = this.refs.cartItemRows?.[line - 1];
  if (!lineItemRow) return;

  const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
  textComponent?.shimmer();
}

  /**
   * Handles the line item removal.
   * @param {number} line
   */
  onLineItemRemove(line) {
  this.updateQuantity({
    line,
    quantity: 0,
    action: 'clear',
  });

  const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];
  if (!cartItemRowToRemove) return;

  const rowsToRemove = [
    cartItemRowToRemove,
    ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
  ];

  const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;

  const template = document.getElementById('empty-cart-template');
  if (isEmptyCart && template instanceof HTMLTemplateElement) {
    const clone = document.importNode(template.content, true);

    startViewTransition(() => {
      this.replaceChildren(clone);
    }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

    return;
  }

  rowsToRemove.forEach((row) => {
    const remove = () => row.remove();

    if (prefersReducedMotion()) return remove();

    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');

    onAnimationEnd(row, remove);
  });
}

  /**
   * Applies loading state to a cart card.
   * @param {HTMLElement | null} card
   * @param {string} message
   */
  #setCardLoading() {
  this.classList.add('is-updating');
}

  /**
   * Clears loading state from a cart card.
   * @param {HTMLElement | null} card
   */
  #clearCardLoading() {
  setTimeout(() => {
    this.classList.remove('is-updating');
  }, 350);
}

  /**
   * Switches one-time purchase line item to subscription.
   * @param {Event} event
   */
  async onSubscribeUpsell(event) {
    const button =
      event.target instanceof Element
        ? event.target.closest('.cart-item-card__subscribe-upsell')
        : null;

    if (!(button instanceof HTMLButtonElement)) return;

    const card = button.closest('.cart-item-card');
   const lineIndex = Number(button.dataset.lineIndex);
const itemKey = String(button.dataset.itemKey || '').trim();
const quantity = Number(button.dataset.quantity);
const variantId = Number(button.dataset.variantId);
const sellingPlanId = String(button.dataset.sellingPlanId || '').trim();

    if (!itemKey || !quantity || !variantId || !sellingPlanId) {
      console.error('Missing subscription upsell data', {
        lineIndex,
        quantity,
        variantId,
        sellingPlanId,
      });
      return;
    }

    const originalHtml = button.innerHTML;
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker('subscribe-upsell:user-action');

    try {
      this.#disableCartItems();
      button.disabled = true;
      button.classList.add('is-loading');
      this.#setCardLoading();

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);

      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sections = Array.from(sectionsToUpdate).join(',');

      this.refs.cartTotal?.shimmer();

      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', String(quantity));
      formData.append('selling_plan', sellingPlanId);
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      const addCart = await addResponse.json();

      if (!addResponse.ok || addCart.errors || addCart.status) {
        console.error('Failed adding subscription line', addCart);
        return;
      }

      const removeResponse = await fetch(
  Theme.routes.cart_update_url,
  fetchConfig('json', {
    body: JSON.stringify({
      updates: {
        [itemKey]: 0,
      },
      sections,
      sections_url: window.location.pathname,
    }),
  })
);

      const removeCart = await removeResponse.json();

      if (removeCart.errors) {
        console.error('Failed removing original OTP line after subscription add', removeCart.errors);
        return;
      }

      resetShimmer(this);

      if (removeCart?.items) {
        this.#updateQuantitySelectors(removeCart);
      }

      if (removeCart.sections?.[this.sectionId]) {
        morphSection(this.sectionId, removeCart.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }

      this.dispatchEvent(
        new CartUpdateEvent(removeCart, this.sectionId, {
          itemCount: removeCart.item_count,
          source: 'cart-items-component',
          sections: removeCart.sections,
        })
      );
    } catch (error) {
      console.error('Failed to switch cart item to subscription', error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      button.disabled = false;
      button.classList.remove('is-loading');
      button.innerHTML = originalHtml;
      this.#clearCardLoading();
      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
    }
  }

  /**
   * Upgrades an active subscription line to the 3-box quarterly subscription.
   * @param {Event} event
   */
  async onUpgradeToQuarterly(event) {
    const button =
      event.target instanceof Element
        ? event.target.closest('.cart-upgrade-card__button')
        : null;

    if (!(button instanceof HTMLButtonElement)) return;

  
    const lineIndex = Number(button.dataset.lineIndex);
const itemKey = String(button.dataset.itemKey || '').trim();
const upgradeVariantId = Number(button.dataset.upgradeVariantId);
const upgradeQuantity = Number(button.dataset.upgradeQuantity || 1);
const quarterlyPlanId = String(button.dataset.upgradeSellingPlanId || '').trim();

    if (!itemKey || !upgradeVariantId || !upgradeQuantity || !quarterlyPlanId) {
      console.error('Missing quarterly upgrade data', {
        lineIndex,
        upgradeVariantId,
        upgradeQuantity,
        quarterlyPlanId,
      });
      return;
    }

    const originalText = button.textContent;
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker('quarterly-upgrade:user-action');

    try {
      this.#disableCartItems();
      button.disabled = true;
      this.#setCardLoading();

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);

      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sections = Array.from(sectionsToUpdate).join(',');

      this.refs.cartTotal?.shimmer();

      const formData = new FormData();
      formData.append('id', String(upgradeVariantId));
      formData.append('quantity', String(upgradeQuantity));
      formData.append('selling_plan', quarterlyPlanId);
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      const addCart = await addResponse.json();

      if (!addResponse.ok || addCart.errors || addCart.status) {
        console.error('Failed adding quarterly upgrade line', addCart);
        return;
      }

      const removeResponse = await fetch(
  Theme.routes.cart_update_url,
  fetchConfig('json', {
    body: JSON.stringify({
      updates: {
        [itemKey]: 0,
      },
      sections,
      sections_url: window.location.pathname,
    }),
  })
);

      const removeCart = await removeResponse.json();

      if (removeCart.errors) {
        console.error('Failed removing original subscription line after quarterly add', removeCart.errors);
        return;
      }

      resetShimmer(this);

      if (removeCart?.items) {
        this.#updateQuantitySelectors(removeCart);
      }

      if (removeCart.sections?.[this.sectionId]) {
        morphSection(this.sectionId, removeCart.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }

      this.dispatchEvent(
        new CartUpdateEvent(removeCart, this.sectionId, {
          itemCount: removeCart.item_count,
          source: 'cart-items-component',
          sections: removeCart.sections,
        })
      );
    } catch (error) {
      console.error('Failed to upgrade subscription to quarterly', error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      button.disabled = false;
      button.textContent = originalText || 'upgrade to 3-box repeat';
      this.#clearCardLoading();
      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
    }
  }

  /**
   * Updates the quantity.
   * @param {{ line: number, quantity: number, action: 'change' | 'clear' }} config
   */
  updateQuantity(config) {
  const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`cart-items:${config.action}:user-action`);

  this.#disableCartItems();

  const { line, quantity } = config;
  const { cartTotal } = this.refs;

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionsToUpdate = new Set([this.sectionId]);

  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });

  const body = JSON.stringify({
    line,
    quantity,
    sections: Array.from(sectionsToUpdate).join(','),
    sections_url: window.location.pathname,
  });

  cartTotal?.shimmer();

  fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
    .then((response) => response.text())
    .then((responseText) => {
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        return;
      }

      if (parsedResponseText?.items) {
        this.#updateQuantitySelectors(parsedResponseText);
      }

      const sectionHtml = parsedResponseText?.sections?.[this.sectionId];

      if (sectionHtml) {
        const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(
          this.sectionId,
          sectionHtml,
          this.isDrawer ? 'hydration' : 'full'
        );

        this.#updateCartQuantitySelectorButtonStates();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    })
    .catch((error) => {
      console.error(error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    })
    .finally(() => {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    });
}

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line
   * @param {{ errors: string }} parsedResponseText
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles cart update events.
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {{ items?: Array<{ variant_id: number, quantity: number }> }} updatedCart
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string}
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean}
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}